import { useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { admin, getErrorMessage } from '../../utils/api';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Loader2, FileWarning, Play } from 'lucide-react';

const ISSUE_LABEL = {
  missing_tax_log: 'Missing tax log',
  tax_amount_mismatch: 'Tax mismatch',
  total_mismatch: 'Total mismatch',
  stripe_mismatch: 'Stripe mismatch',
};

function lastMonths(n) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    out.push(`${y}-${m}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

export default function AdminTaxExceptions({ user, onLogout }) {
  const months = lastMonths(6);
  const [period, setPeriod] = useState(months[0]);
  const [checkStripe, setCheckStripe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const res = await admin.taxExceptions(period, checkStripe);
      setResult(res.data);
      if (res.data.exception_count === 0) toast.success('No exceptions — all orders reconciled ✓');
      else toast.warning(`${res.data.exception_count} exception(s) found`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to run exception check'));
    } finally {
      setLoading(false);
    }
  };

  const s = result?.summary || {};
  const cards = [
    { key: 'missing_tax_log', label: 'Missing Tax Log', color: 'text-red-600' },
    { key: 'tax_amount_mismatch', label: 'Tax Mismatch', color: 'text-orange-600' },
    { key: 'total_mismatch', label: 'Total Mismatch', color: 'text-amber-600' },
    { key: 'stripe_mismatch', label: 'Stripe Mismatch', color: 'text-purple-600' },
  ];

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-tax-exceptions">
        <div className="bg-gradient-to-r from-rose-600 to-red-700 rounded-2xl p-8 text-white shadow-lg">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Reconciliation Exceptions</h2>
          <p className="text-rose-100 text-lg">Catch orders missing a tax log or whose tax/total drifts from the ledger (and, optionally, from Stripe) before you file.</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Period</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-40" data-testid="exceptions-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch id="stripe-check" checked={checkStripe} onCheckedChange={setCheckStripe} data-testid="exceptions-stripe-toggle" />
                <Label htmlFor="stripe-check" className="text-sm">Also check Stripe amounts</Label>
              </div>
              <Button onClick={run} disabled={loading} className="bg-rose-600 hover:bg-rose-700" data-testid="exceptions-run-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Run Check
              </Button>
            </div>
            {checkStripe && (
              <p className="text-xs text-gray-500 mt-3">Stripe amount checks run live via each order's payment intent — only where a valid Stripe key is configured (works in production).</p>
            )}
          </CardContent>
        </Card>

        {result && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {cards.map((c) => (
                <Card key={c.key} data-testid={`exception-count-${c.key}`}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">{c.label}</p>
                    <p className={`text-3xl font-bold ${c.color}`}>{s[c.key] ?? 0}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileWarning className="w-5 h-5 text-rose-600" />
                  Exceptions — {result.period_yyyymm}
                </CardTitle>
                <CardDescription>
                  {result.order_count} order(s) scanned · {result.exception_count} exception(s)
                  {result.stripe_enabled ? ` · ${result.stripe_checked} Stripe lookups` : ''}
                </CardDescription>
                {result.stripe_enabled && result.stripe_checked > 0 && result.stripe_failed === result.stripe_checked && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="stripe-check-warning">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>All Stripe amount lookups failed (invalid or unreachable key) — order amounts were NOT verified against Stripe. This is expected in preview; it works in production.</span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {result.exception_count === 0 ? (
                  <div className="text-center py-10">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-500">All orders reconciled for {result.period_yyyymm}.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-2 pr-4">Order</th>
                          <th className="py-2 pr-4">Retailer</th>
                          <th className="py-2 pr-4">Issues</th>
                          <th className="py-2 pr-4 text-right">Order Tax</th>
                          <th className="py-2 pr-4 text-right">Ledger Tax</th>
                          <th className="py-2 pr-4 text-right">Order Total</th>
                          <th className="py-2 pr-4 text-right">Ledger Total</th>
                          <th className="py-2 pr-4 text-right">Stripe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.exceptions.map((ex) => (
                          <tr key={ex.order_id} className="border-b hover:bg-gray-50" data-testid={`exception-row-${ex.order_id}`}>
                            <td className="py-2 pr-4 font-mono text-xs">{(ex.order_id || '').slice(0, 8)}</td>
                            <td className="py-2 pr-4 text-gray-700">{ex.drlp_name || '—'}</td>
                            <td className="py-2 pr-4">
                              <div className="flex flex-wrap gap-1">
                                {ex.issues.map((i) => (
                                  <Badge key={i} className="bg-red-100 text-red-700 text-xs flex items-center">
                                    <AlertTriangle className="w-3 h-3 mr-1" />{ISSUE_LABEL[i] || i}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="py-2 pr-4 text-right">${(ex.order_tax ?? 0).toFixed(2)}</td>
                            <td className="py-2 pr-4 text-right">{ex.log_tax == null ? '—' : `$${ex.log_tax.toFixed(2)}`}</td>
                            <td className="py-2 pr-4 text-right">${(ex.order_total ?? 0).toFixed(2)}</td>
                            <td className="py-2 pr-4 text-right">{ex.log_total == null ? '—' : `$${ex.log_total.toFixed(2)}`}</td>
                            <td className="py-2 pr-4 text-right">{ex.stripe_amount == null ? '—' : `$${ex.stripe_amount.toFixed(2)}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
